import React, { useCallback, useEffect, useRef } from 'react';
import '../mail/SendMailScreen.css';
import '../pages/RoundMail.css';
import './MailAudienceEditor.css';
import Icon from '../home/Icon';
import Textfield from '../input/Textfield';
import ActionButton from '../input/ActionButton';
import Tooltip from '../tooltip/Tooltip';
import { isPersonalLinkGroup } from '../../util/participantGroups';
import ProcessNotice from './ProcessNotice';
import { NEWSLETTER_TARGET_KEY } from './processUtils';

import { useEditor, BubbleMenu, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HardBreak from '@tiptap/extension-hard-break';

// Rich text body of one audience (same TipTap setup + bubble menu as the former inline mail editor).
// Mounted only while the accordion item is open; `contentVersion` changes when a new preview replaced
// the texts, then the editor takes over the draft again.
function MailBodyEditor({ body, contentVersion, onChange, ariaLabel }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const bodyRef = useRef(body);
  bodyRef.current = body;
  const versionRef = useRef(contentVersion);

  const editor = useEditor({
    extensions: [
      Document, Paragraph, Text, BulletList, ListItem, Bold, Italic,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      Underline, History, Color, TextStyle, HardBreak,
    ],
    content: body ?? '',
    editorProps: { attributes: { 'aria-label': ariaLabel } },
    onUpdate: ({ editor: ed }) => onChangeRef.current(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor || versionRef.current === contentVersion) return;
    versionRef.current = contentVersion;
    editor.commands.setContent(bodyRef.current ?? '', false);
  }, [editor, contentVersion]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className={'mailAudienceBody'}>
      <BubbleMenu editor={editor}>
        <div className={'bubbleMenu'}>
          <Tooltip text={'Rückgängig'}>
            <button type={'button'} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} aria-label={'Rückgängig'}>
              <Icon className={'bubbleIcon'} icon={'undo'} />
            </button>
          </Tooltip>
          <Tooltip text={'Wiederholen'}>
            <button type={'button'} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} aria-label={'Wiederholen'}>
              <Icon className={'bubbleIcon'} icon={'redo'} />
            </button>
          </Tooltip>
          <Tooltip text={'Fett'}>
            <button type={'button'} onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'isActive' : ''} aria-label={'Fett'}>
              <Icon className={'bubbleIcon'} icon={'format_bold'} />
            </button>
          </Tooltip>
          <Tooltip text={'Kursiv'}>
            <button type={'button'} onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'isActive' : ''} aria-label={'Kursiv'}>
              <Icon className={'bubbleIcon'} icon={'format_italic'} />
            </button>
          </Tooltip>
          <Tooltip text={'Link'}>
            <button type={'button'} onClick={setLink} className={editor.isActive('link') ? 'isActive' : ''} aria-label={'Link'}>
              <Icon className={'bubbleIcon'} icon={'link'} />
            </button>
          </Tooltip>
          <Tooltip text={'Liste'}>
            <button type={'button'} onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'isActive' : ''} aria-label={'Liste'}>
              <Icon className={'bubbleIcon'} icon={'format_list_bulleted'} />
            </button>
          </Tooltip>
          <Tooltip text={'Farbe'}>
            <input
              type={'color'}
              aria-label={'Farbe'}
              onInput={e => editor.chain().focus().setColor(e.target.value).run()}
              value={editor.getAttributes('textStyle').color || '#000000'}
              onChange={() => {}}
            />
          </Tooltip>
          <Tooltip text={'Farbe zurücksetzen'}>
            <button type={'button'} onClick={() => editor.chain().focus().unsetColor().run()} aria-label={'Farbe zurücksetzen'}>
              <Icon className={'bubbleIcon'} icon={'format_color_reset'} />
            </button>
          </Tooltip>
        </div>
      </BubbleMenu>
      <EditorContent editor={editor} />
    </div>
  );
}

// Placeholder hint per audience: personal link for personal-link groups, unsubscribe link for the
// newsletter, the general per-recipient placeholders otherwise.
function placeholderHint(targetKey, group) {
  if (targetKey === NEWSLETTER_TARGET_KEY) {
    return <>Der Platzhalter <code>{'{NewsletterUnsubscribeLink}'}</code> wird beim Versand pro Empfänger durch den persönlichen Abmelde-Link ersetzt.</>;
  }
  if (isPersonalLinkGroup(group)) {
    return <>Der Platzhalter <code>{'{PersoenlicherLink}'}</code> wird beim Versand pro Empfänger durch den persönlichen Anmelde-Link ersetzt.</>;
  }
  return <>Platzhalter wie <code>{'{Vorname}'}</code> oder <code>{'{PreisEintrittGruppe}'}</code> werden beim Versand pro Empfänger ersetzt.</>;
}

// Audience without a textblock (no default textblock anymore): cause + how to fix it.
function missingTextblockTexts(isNewsletter, audience) {
  return isNewsletter
    ? {
      cause: 'Der Schritt hat für den Newsletter keinen Textbaustein – die Mail kann nicht gesendet werden.',
      fix: 'Unter „Schritte anpassen“ einen Textbaustein für den Newsletter wählen.',
    }
    : {
      cause: `Weder der Schritt noch die Mail-Regel „Ankündigung“ hat einen Textbaustein – an ${audience.targetName} kann nicht gesendet werden.`,
      fix: 'Unter „Schritte anpassen“ einen Textbaustein wählen oder ihn in der Mail-Regel „Ankündigung“ der Gruppe bzw. der Prozessvorlage hinterlegen.',
    };
}

/**
 * One accordion item of the SendMail panel (decision 5): header = target name + recipient count
 * (+ newsletter dedup), body = editable subject + text + "Testmail an mich".
 * missingTextblock: the preview resolved no textblock for this audience — the item only shows why it
 * cannot be sent (missingText = the server's preview error for it, if any).
 */
function MailAudienceEditor({
  audience, target, group, draft, contentVersion, expanded, onToggle, onChange, onTestMail,
  testSending = false, testError = '', disabled = false, missingTextblock = false, missingText = '',
}) {
  const bodyId = `mailAudience-${audience.targetKey.replace(/[^a-z0-9]/gi, '-')}`;
  const isNewsletter = audience.targetKey === NEWSLETTER_TARGET_KEY;
  const textblockName = target?.textblockName;
  // Set when the preview used the textblock's variant for the process location instead of the default.
  const variantLocationName = audience.variantLocationName;
  const missing = missingTextblock ? missingTextblockTexts(isNewsletter, audience) : null;

  return (
    <div className={`mailAudience${expanded ? ' isExpanded' : ''}${missing ? ' hasError' : ''}`}>
      <button
        type={'button'}
        className={'mailAudienceHeader'}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className={'material-symbols-rounded mailAudienceChevron'} aria-hidden={'true'}>expand_more</span>
        <span className={'material-symbols-rounded mailAudienceIcon'} aria-hidden={'true'}>{isNewsletter ? 'campaign' : 'group'}</span>
        <span className={'mailAudienceTitle'}>
          <span className={'mailAudienceName'}>{audience.targetName}</span>
          {isNewsletter && audience.dedupCount > 0 && (
            <span className={'mailAudienceDedup'}>{audience.dedupCount} bereits als Spieler erreicht</span>
          )}
        </span>
        {missing && (
          <span className={'mailAudienceIssue'}>
            <span className={'material-symbols-rounded'} aria-hidden={'true'}>error</span>
            Kein Textbaustein
          </span>
        )}
        <span className={`mailAudienceCount${audience.recipientCount === 0 ? ' isEmpty' : ''}`}>
          {audience.recipientCount} Empfänger
        </span>
      </button>

      {expanded && missing && (
        <div className={'mailAudienceContent'} id={bodyId}>
          <ProcessNotice tone={'error'} title={'Kein Textbaustein zugewiesen'} className={'mailAudienceMissing'}>
            <p>{missingText || missing.cause}</p>
            <p>{missing.fix}</p>
          </ProcessNotice>
        </div>
      )}

      {expanded && !missing && (
        <div className={'mailAudienceContent'} id={bodyId}>
          {/* div, not p: the Tooltip renders a div. */}
          <div className={'mailAudienceSource'}>
            <span>
              {textblockName
                ? <>Textbaustein: <b>{textblockName}</b></>
                // Without a step textblock a group target uses its mail rule "Ankündigung" (template rule
                // for the group / all groups, else the group's own); the newsletter always has its own.
                : (isNewsletter ? 'Textbaustein des Schritts' : 'Textbaustein aus der Mail-Regel „Ankündigung“')}
            </span>
            {variantLocationName && (
              <Tooltip text={`Für ${variantLocationName} gibt es eine eigene Variante des Textbausteins – Betreff und Text kommen aus ihr.`}>
                <span className={'mailAudienceVariant'}>
                  <span className={'material-symbols-rounded'} aria-hidden={'true'}>location_on</span>
                  Variante {variantLocationName}
                </span>
              </Tooltip>
            )}
          </div>
          <Textfield
            icon={'subject'}
            type={'text'}
            label={'Betreff'}
            placeholder={'Ankündigung: Spieltag 24.06.'}
            value={draft?.subject ?? ''}
            disabled={disabled}
            onChange={e => onChange({ subject: e.target.value })}
          />
          <MailBodyEditor
            body={draft?.body ?? ''}
            contentVersion={contentVersion}
            onChange={html => onChange({ body: html })}
            ariaLabel={`Mailtext für ${audience.targetName}`}
          />
          <div className={'roundmailPlaceholderInfo'}>
            <Icon icon={'info'} className={'roundmailInfoIcon'} />
            <span>{placeholderHint(audience.targetKey, group)}</span>
          </div>
          {testError && <p className={'mailAudienceError'}>{testError}</p>}
          <div className={'mailAudienceActions'}>
            <ActionButton
              icon={'forward_to_inbox'}
              label={testSending ? 'Testmail wird gesendet …' : 'Testmail an mich'}
              onClick={onTestMail}
              disabled={disabled || testSending}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MailAudienceEditor;
